import React, { useState, useEffect, useRef } from 'react';

function QuestionMedia({ src, alt, className, preloadNext, mediaType = 'image', thumbnailUrl }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true); // Control thumbnail visibility
  const [isMuted, setIsMuted] = useState(() => {
    // Load mute preference from localStorage, default to true (muted)
    const saved = localStorage.getItem('videoMuted');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const videoRef = useRef(null);
  const thumbnailRef = useRef(null);

  // Toggle mute and save preference
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    localStorage.setItem('videoMuted', JSON.stringify(newMutedState));

    // Update the video element if it exists
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  };

  useEffect(() => {
    // Reset states when src changes
    setLoading(true);
    setError(false);
    setShowThumbnail(true); // Show thumbnail initially

    if (mediaType === 'video') {
      // For videos, wait for canplay event
      const video = videoRef.current;
      if (video) {
        // Apply muted state to video element
        video.muted = isMuted;

        const handleCanPlay = () => {
          setLoading(false);

          // Wait a moment for the video to start playing, then fade out thumbnail
          setTimeout(() => {
            setShowThumbnail(false);
          }, 100); // Small delay to ensure video is ready

          // Preload next media if provided
          if (preloadNext) {
            const nextVideo = document.createElement('video');
            nextVideo.src = preloadNext;
            nextVideo.preload = 'auto';
          }
        };

        const handleError = () => {
          setLoading(false);
          setError(true);
          console.error('Failed to load video:', src);
        };

        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);

        return () => {
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
        };
      }
    } else {
      // For images, use Image preloading
      const img = new Image();
      img.src = src;

      img.onload = () => {
        setLoading(false);

        // Preload next image if provided
        if (preloadNext) {
          const nextImg = new Image();
          nextImg.src = preloadNext;
        }
      };

      img.onerror = () => {
        setLoading(false);
        setError(true);
        console.error('Failed to load image:', src);
      };
    }
  }, [src, preloadNext, mediaType, isMuted]);

  return (
    <>
      {loading && (
        <div
          className="image-loading-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#111',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}
        >
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255,255,255,0.2)',
            borderTop: '3px solid rgba(255,255,255,0.8)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
      {mediaType === 'video' ? (
        <>
          {/* Video element - always visible once loaded */}
          <video
            ref={videoRef}
            src={src}
            className={className}
            style={{
              opacity: loading ? 0 : 1,
              transition: 'opacity 0.5s ease-in-out',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            onError={() => setError(true)}
          />

          {/* Thumbnail overlay - fades out when video is ready */}
          {thumbnailUrl && (
            <img
              ref={thumbnailRef}
              src={thumbnailUrl}
              alt={alt}
              className={className}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: showThumbnail ? 1 : 0,
                transition: 'opacity 0.8s ease-out',
                pointerEvents: 'none',
                zIndex: showThumbnail ? 3 : 0
              }}
            />
          )}

          {/* Mute/Unmute Button */}
          <button
            onClick={toggleMute}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 10,
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
        </>
      ) : (
        <img
          src={src}
          alt={alt}
          className={className}
          style={{
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}
          onError={() => setError(true)}
        />
      )}
    </>
  );
}

export default QuestionMedia;
